import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { PrintDialogComponent, PrintPageType, PrintType } from '@components/print-dialog/print-dialog.component';
import { SensorCableTagService } from './sensor-cable-tag.service';
import { SensorCableTagPrintAPIService } from './sensor-cable-tag-print-api.service';
import { LoadingDialogComponent } from '@layout/loading-dialog/loading-dialog.component';

@Injectable({ providedIn: 'root' })
export class PrintSensorCableService {
    constructor(
        private sensorCableTagService: SensorCableTagService,
        private sensorTagPrint: SensorCableTagPrintAPIService,
        private dialog: MatDialog
    ) {}

    tableWidths = [40, 80, 150, 150, 100];

    async triggerFCUPrint(vessel, comp: boolean = false): Promise<boolean> {
        let loadingRef = null;

        try {
            let vesselSlots = [];
            const slots: any[] = await this.sensorCableTagService.getVesselSlots(vessel.id);
            if (slots?.length > 0) {
                vesselSlots = slots.map(slot => ({
                    no: slot.no,
                    slotId: slot.id,
                    ioType: slot.ioType,
                    fcuNum: slot.FcuUnit?.index,
                    fuNum: slot.FuUnit?.index,
                    terminal: slot.terminal,
                    vesselId: vessel.id,
                }));
            }

            const fcuNums = [...new Set((vesselSlots ?? []).map(slot => slot.fcuNum).filter(Boolean))].sort(
                (a, b) => a - b
            );
            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.SENSOR_CABLE_TAG,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    options: fcuNums,
                    notMain: comp,
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;
            const { printType, setListIds } = shouldPrint;
            if (printType !== PrintType.NO_UPDATES) return true;

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: { progress: 0 },
            });

            loadingRef.componentInstance.data.progress = 30;

            const pdfFile = await this.sensorTagPrint.getFuPrintFile(vessel.id, setListIds);

            loadingRef.componentInstance.data.progress = 70;

            const blob = new Blob([pdfFile], { type: 'application/pdf' });

            loadingRef.componentInstance.data.progress = 100;
            loadingRef.close();
            loadingRef = null;

            await new Promise<void>((resolve, reject) => {
                try {
                    const blobUrl = URL.createObjectURL(blob);
                    const newWindow = window.open(blobUrl, '_blank');
                    if (!newWindow) {
                        reject(new Error('Popup blocker prevented opening the new window.'));
                        return;
                    }
                    newWindow.onload = () => {
                        try {
                            newWindow.print();
                        } catch (e) {
                            console.error('Failed to trigger print dialog:', e);
                        }
                    };
                    resolve();

                    setTimeout(() => {
                        URL.revokeObjectURL(blobUrl);
                    }, 300000);
                } catch (blobError) {
                    reject(blobError);
                }
            });

            return true;
        } catch (err) {
            console.error('Error in triggerFCUPrint:', err);
            throw err;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
        }
    }

    async triggerFUPrint(vessel): Promise<boolean> {
        let loadingRef = null;

        try {
            let vesselSlots = [];
            const slots: any[] = await this.sensorCableTagService.getVesselSlots(vessel.id);
            if (slots?.length > 0) {
                vesselSlots = slots.map(slot => ({
                    no: slot.no,
                    slotId: slot.id,
                    ioType: slot.ioType,
                    fcuNum: slot.FcuUnit?.index,
                    fuNum: slot.FuUnit?.index,
                    terminal: slot.terminal,
                    vesselId: vessel.id,
                }));
            }

            const fcuNums = [...new Set((vesselSlots ?? []).map(slot => slot.fcuNum).filter(Boolean))].sort(
                (a, b) => a - b
            );

            const fcuWithFuList = fcuNums.map(fcu => {
                const fuList = [
                    ...new Set(
                        vesselSlots
                            .filter(slot => slot.fcuNum === fcu)
                            .map(slot => slot.fuNum)
                            .filter(Boolean)
                    ),
                ].sort((a, b) => a - b);

                return {
                    fcuNum: fcu,
                    fuList: fuList,
                };
            });

            const dialogRef = this.dialog.open(PrintDialogComponent, {
                data: {
                    pageType: PrintPageType.SENSOR_FU_PRINT,
                    vessel: vessel,
                    iAPIVesselId: vessel.iAPIVesselId,
                    shipyardId: vessel.shipyardId,
                    options: fcuNums,
                    extraPreOptions: fcuWithFuList,
                    extraPreField: 'fcu',
                },
            });

            const shouldPrint = await lastValueFrom(dialogRef.afterClosed());
            if (!shouldPrint) return true;
            if (shouldPrint.cancel) return false;
            const { printType, setListIds } = shouldPrint;

            loadingRef = this.dialog.open(LoadingDialogComponent, {
                width: '320px',
                disableClose: true,
                data: { progress: 0 },
            });

            loadingRef.componentInstance.data.progress = 30;

            const pdfFile = await this.sensorTagPrint.getSelectedFuPrintFile(vessel.id, setListIds);

            loadingRef.componentInstance.data.progress = 70;

            const blob = new Blob([pdfFile], { type: 'application/pdf' });

            loadingRef.componentInstance.data.progress = 100;
            loadingRef.close();
            loadingRef = null;

            await new Promise<void>((resolve, reject) => {
                try {
                    const blobUrl = URL.createObjectURL(blob);
                    const newWindow = window.open(blobUrl, '_blank');
                    if (!newWindow) {
                        reject(new Error('Popup blocker prevented opening the new window.'));
                        return;
                    }
                    newWindow.onload = () => {
                        try {
                            newWindow.print();
                        } catch (e) {
                            console.error('Failed to trigger print dialog:', e);
                        }
                    };
                    resolve();

                    setTimeout(() => {
                        URL.revokeObjectURL(blobUrl);
                    }, 300000);
                } catch (blobError) {
                    reject(blobError);
                }
            });

            return true;
        } catch (err) {
            console.error('Error in triggerFUPrint:', err);
            throw err;
        } finally {
            if (loadingRef) {
                loadingRef.close();
            }
        }
    }
}
